import { Link } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Instagram, Facebook, Youtube, Mail } from "lucide-react";
import { CATEGORY_NAV, BRAND_NAV } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export const Footer = () => {
  const { t, tx } = useI18n();
  const onSubscribe = (e) => {
    e.preventDefault();
    const email = new FormData(e.currentTarget).get("email");
    if (email) {
      toast.success("Thanks for subscribing!", { description: "You'll hear about new Kentucky arrivals & offers." });
      e.currentTarget.reset();
    }
  };
  return (
    <footer data-testid="site-footer" className="mt-20 bg-header text-white/90">
      <div className="container-site grid grid-cols-2 gap-8 py-14 md:grid-cols-4 lg:grid-cols-5">
        <div className="col-span-2 lg:col-span-2">
          <img src="/boomerang-logo.webp" alt="Boomerang.ie" className="h-12 w-auto" />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/70">
            {t("footer.tagline")}
          </p>
          <div className="mt-5 flex gap-3">
            <a href="https://www.instagram.com/boomerang.ie/" aria-label="Instagram" target="_blank" rel="noopener noreferrer" className="rounded-full border border-white/15 p-2 hover:bg-white/10"><Instagram className="h-4 w-4" /></a>
            <a href="https://www.facebook.com/Boomeranghorseandcountry/" aria-label="Facebook" target="_blank" rel="noopener noreferrer" className="rounded-full border border-white/15 p-2 hover:bg-white/10"><Facebook className="h-4 w-4" /></a>
            <a href="https://www.youtube.com/@BoomerangHorseCountry/videos" aria-label="YouTube" target="_blank" rel="noopener noreferrer" className="rounded-full border border-white/15 p-2 hover:bg-white/10"><Youtube className="h-4 w-4" /></a>
            <a href="mailto:shop@boomerang.ie" aria-label="Email" className="rounded-full border border-white/15 p-2 hover:bg-white/10"><Mail className="h-4 w-4" /></a>
          </div>
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white">{t("footer.shop")}</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-white/70">
            {CATEGORY_NAV.map((c) => (
              <li key={c.slug}><LangLink to={`/collection/${c.slug}`} className="hover:text-white">{tx(c.name)}</LangLink></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white">{t("footer.brands_help")}</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-white/70">
            {BRAND_NAV.map((b) => (
              <li key={b.slug}><LangLink to={`/brand/${b.slug}`} className="hover:text-white">{b.name}</LangLink></li>
            ))}
            <li><LangLink to="/shipping" className="hover:text-white">{t("footer.shipping_delivery")}</LangLink></li>
            <li><LangLink to="/size-guide" className="hover:text-white">{t("nav.size_guide")}</LangLink></li>
          </ul>
        </div>
        <div className="col-span-2 lg:col-span-1">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-white">{t("footer.newsletter")}</h4>
          <p className="mt-4 text-sm text-white/70">{t("footer.newsletter_desc")}</p>
          <form onSubmit={onSubscribe} data-testid="newsletter-form" className="mt-3 flex gap-2">
            <input name="email" type="email" required placeholder={t("footer.email_placeholder")} data-testid="newsletter-input"
              className="min-w-0 flex-1 rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-boomerang" />
            <Button type="submit" data-testid="newsletter-submit-button">{t("footer.join")}</Button>
          </form>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-site grid grid-cols-2 gap-8 py-10 sm:grid-cols-3">
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white">{tx("Shop Collections")}</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              <li><LangLink to="/kentucky-saddle-pads" className="hover:text-white">{tx("Kentucky Saddle Pads")}</LangLink></li>
              <li><LangLink to="/custom-saddle-pads" className="hover:text-white">{tx("Custom Saddle Pads")}</LangLink></li>
              <li><LangLink to="/grooming-deluxe" className="hover:text-white">{tx("Grooming Deluxe")}</LangLink></li>
              <li><LangLink to="/kentucky-dressage" className="hover:text-white">{tx("Dressage Collection")}</LangLink></li>
              <li><LangLink to="/how-to-choose-a-saddle-pad" className="hover:text-white">{tx("Saddle Pad Buying Guide")}</LangLink></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white">{tx("New & Seasonal")}</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              <li><LangLink to="/spring-summer-2026" className="hover:text-white">{tx("Spring / Summer 2026")}</LangLink></li>
              <li><LangLink to="/collection/fly-veils" className="hover:text-white">{tx("Fly Protection")}</LangLink></li>
              <li><LangLink to="/express-delivery" className="hover:text-white">{tx("Express Delivery")}</LangLink></li>
              <li><LangLink to="/brand/kentucky" className="hover:text-white">{tx("New Arrivals")}</LangLink></li>
            </ul>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-white">{tx("Shop by Region")}</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              <li><LangLink to="/kentucky-horsewear-ireland" className="hover:text-white">{tx("Kentucky Horsewear Ireland")}</LangLink></li>
              <li><LangLink to="/kentucky-horsewear-usa" className="hover:text-white">{tx("Kentucky Horsewear USA")}</LangLink></li>
              <li><LangLink to="/kentucky-horsewear-canada" className="hover:text-white">{tx("Kentucky Horsewear Canada")}</LangLink></li>
              <li><LangLink to="/kentucky-horsewear-germany" className="hover:text-white">{tx("Kentucky Horsewear Germany")}</LangLink></li>
              <li><LangLink to="/kentucky-horsewear-netherlands" className="hover:text-white">{tx("Kentucky Horsewear Netherlands")}</LangLink></li>
              <li><LangLink to="/kentucky-horsewear-france" className="hover:text-white">{tx("Kentucky Horsewear France")}</LangLink></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-site flex flex-col gap-3 py-5 text-[11px] sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-white/55">
            <span className="font-mono uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{tx("Payment")}</span>
            <span>Klarna</span><span>Humm</span><span>Visa</span><span>Mastercard</span><span>PayPal</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-white/55">
            <span className="font-mono uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{tx("Delivery")}</span>
            <span>DPD</span><span>DHL</span><span>UPS</span><span>An Post</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/55">
            <span className="font-mono uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{tx("Rewards")}</span>
            <span>{tx("Earn Bon loyalty points on every order")}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-site flex flex-col items-center justify-between gap-3 py-5 text-xs text-white/50 sm:flex-row">
          <div className="text-center sm:text-left">
            <p>© {new Date().getFullYear()} Boomerang.ie Horse &amp; Country. {tx("Official Kentucky Horsewear reseller.")}</p>
            <p className="mt-1 text-white/40">{tx("Company No.")} 5283148 · {tx("VAT No.")} IE3191391SH</p>
          </div>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
            <LangLink to="/about" className="hover:text-white/80">{tx("About")}</LangLink>
            <LangLink to="/contact" className="hover:text-white/80">{tx("Contact")}</LangLink>
            <a href="https://boomerang.ie/pages/terms-conditions" target="_blank" rel="noopener noreferrer" className="hover:text-white/80">{tx("Terms & Conditions")}</a>
            <a href="https://boomerang.ie/pages/returns" target="_blank" rel="noopener noreferrer" className="hover:text-white/80">{tx("Returns")}</a>
            <a href="https://boomerang.ie/pages/customs-duties" target="_blank" rel="noopener noreferrer" className="hover:text-white/80">{tx("Customs & Duties")}</a>
            <a href="/api/sitemap.xml" className="hover:text-white/80">{tx("Sitemap")}</a>
          </div>
        </div>
        <div className="container-site pb-5 text-center text-[11px] leading-relaxed text-white/35 sm:text-left">
          {tx("* Free shipping on orders in Ireland over €100 excludes feed, bedding & certain overweight items. Full details in T&Cs.")}
        </div>
      </div>

      <div className="border-t border-white/10 bg-black/15">
        <div className="container-site py-4 text-center">
          <p className="text-[11px] tracking-[0.18em] text-white/40">
            <span className="uppercase">{tx("Bespoke web experience crafted by")}</span>{" "}
            <a
              href="https://www.intellixit.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="intellix-credit-link"
              className="font-medium uppercase tracking-[0.18em] text-white/70 underline-offset-4 transition-colors duration-200 hover:text-[hsl(var(--primary))] hover:underline"
            >
              Intellix IT Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
